/**
 * rateTeamStore.js — the only thing /rate's team tab remembers between visits.
 *
 * It also holds the two other things this surface remembers: which tab was last
 * open, and whether the full team breakdown has been unlocked with an email
 * address (once ever per browser).
 *
 * 🔴 WHY THIS EXISTS. Filling the roster ladder is five minutes of typing on a
 * phone, and every reload, every back button and every accidental tab close
 * threw all of it away. The page was deliberately stateless (see TeamTab's
 * header) and that was right for everything EXCEPT the roster: nothing here is
 * sent anywhere, nothing is shared, and the user is the only reader.
 *
 * 🔴 NAMESPACED BY LEAGUE, ONE SLOT EACH. A person with three saved leagues has
 * three different rosters, and a single shared key would hand them last week's
 * team in this week's league with no sign anything was wrong. Manual entry gets
 * one slot of its own ("manual"), because a manually described league has no
 * name to key on and there is only ever one of it.
 *
 * 🔴 EVERY READ AND EVERY WRITE IS GUARDED, AND A FAILURE IS SILENT. Private
 * mode, a full quota and a disabled-storage browser all land on exactly the
 * behaviour this page had before persistence existed: a blank form that works.
 * A grading page must never show somebody a storage error.
 *
 * 🔴 A CORRUPT RECORD IS NO RECORD. Anything that is not an object with an
 * object `ladder` reads as absent rather than being repaired, because a
 * half-restored roster is worse than an empty one: the user cannot see what is
 * missing, and the report would grade a team they did not enter.
 *
 * 🔴 A LEAGUE HOLDS A LIST OF TEAMS NOW, NOT ONE ROSTER (round 6, Aug 30 2026).
 * The point of /rate's team tab was always one squad; the point of round 6 is a
 * whole room, entered one team at a time. So a slot's record grew from
 * `{ ladder }` to `{ teams: [{ name, ladder }] }`, in the SAME key family: a
 * second key would mean two records for one league and a rule about which one
 * wins.
 *
 * THE MIGRATION IS SILENT AND LOSSLESS. A v1 record reads back as a one-team
 * list called "My team" and the next write persists it in the new shape. Nobody
 * is asked anything and nobody loses a roster. readEntry / writeEntry stay as
 * the single-roster API over the first team, so every caller and test that
 * predates the list keeps working and there is still only one storage format
 * underneath them.
 */

import { isInternalUser, isLocalHost } from "./analyticsGate.js";


/** One key per league. The league name is the namespace. */
export const RATE_TEAM_PREFIX = "auctionArchitect_rateTeam:";

/** Which slot the last visit was in, so a return lands where it left. */
export const RATE_ACTIVE_KEY = "auctionArchitect_rateTeamActive";

/** Which of the page's two tabs was last open. */
export const RATE_TAB_KEY = "auctionArchitect_rateTab";

/**
 * Whether this browser has already paid for the full team breakdown with an
 * email address.
 *
 * 🔴 ONCE EVER, NOT ONCE PER REPORT. Somebody who gave their address for one
 * roster has bought the breakdown, and asking again on their next team would be
 * a toll booth rather than a signup. There is no expiry and no per-league
 * namespace on purpose.
 */
export const RATE_UNLOCK_KEY = "auctionArchitect_rateUnlocked";

/** The slot a manually described league lives in. There is only ever one. */
export const MANUAL_SLOT = "manual";

/** The storage key for one slot. A null or empty league name is the manual slot. */
export function slotKey(leagueName) {
  return `${RATE_TEAM_PREFIX}${leagueName || MANUAL_SLOT}`;
}

function isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/**
 * What the first team in a league is called before anybody renames it.
 *
 * "My team" rather than "Team 1" because it IS the user's own squad: this page
 * exists to grade theirs first, and the rest of the room is what they add
 * afterwards. The teams added after it default to "Team 2", "Team 3" and so on,
 * which reads correctly beside it.
 */
export const DEFAULT_TEAM_NAME = "My team";

/**
 * One slot's teams, or null.
 *
 * A `ladder` is the entry form's own state shape, keyed by slot id. Rows are
 * kept as they were written rather than re-validated field by field:
 * entriesFromLadder already drops anything without a player and a position, so
 * a junk row cannot reach a report even if it reaches this map.
 *
 * `settings` is the manual form's settings and is null for a saved league,
 * whose settings live in the saved league itself and must keep coming from
 * there — a stored copy would go stale the moment the user edits the league.
 *
 * 🔴 A v1 RECORD IS A ONE-TEAM ROOM, and reading one is the whole migration.
 * There is no rewrite pass and no version stamp to chase: the next write puts
 * it in the new shape, and until then it reads correctly. A record whose
 * `teams` is present but junk reads as ABSENT rather than as a partial room,
 * the same rule a corrupt v1 ladder has always had.
 *
 * @returns { teams: [{ name, ladder }], settings } or null
 */
export function readTeams(leagueName) {
  try {
    const raw = JSON.parse(localStorage.getItem(slotKey(leagueName)));
    if (!isPlainObject(raw)) return null;
    const settings = isPlainObject(raw.settings) ? raw.settings : null;
    if (Array.isArray(raw.teams)) {
      const teams = raw.teams
        .filter((t) => isPlainObject(t) && isPlainObject(t.ladder))
        .map((t, i) => ({
          name: typeof t.name === "string" && t.name.trim() ? t.name : nthTeamName(i),
          ladder: t.ladder,
        }));
      return teams.length > 0 ? { teams, settings } : null;
    }
    // v1: one roster, no name. It becomes the user's own team.
    if (isPlainObject(raw.ladder)) {
      return { teams: [{ name: DEFAULT_TEAM_NAME, ladder: raw.ladder }], settings };
    }
    return null;
  } catch {
    return null;
  }
}

/** The name a team gets when nobody has typed one. Index 0 is the user's own. */
export function nthTeamName(i) {
  return i === 0 ? DEFAULT_TEAM_NAME : `Team ${i + 1}`;
}

/** Write one slot's whole team list. A failure leaves the page working, unsaved. */
export function writeTeams(leagueName, { teams, settings }) {
  try {
    localStorage.setItem(
      slotKey(leagueName),
      JSON.stringify({
        v: 2,
        teams: (teams || []).map((t, i) => ({
          name: t && t.name ? t.name : nthTeamName(i),
          ladder: (t && t.ladder) || {},
        })),
        settings: settings || null,
      })
    );
  } catch {
    /* private mode, quota, storage disabled — nothing to do and nothing to say */
  }
}

/**
 * The single-roster API, kept because everything older than round 6 speaks it.
 * It reads and writes the FIRST team, which is the only team a v1 record ever
 * had. Anything that manages a room uses readTeams / writeTeams.
 */
export function readEntry(leagueName) {
  const rec = readTeams(leagueName);
  if (!rec) return null;
  return { ladder: rec.teams[0].ladder, settings: rec.settings };
}

export function writeEntry(leagueName, { ladder, settings }) {
  writeTeams(leagueName, { teams: [{ name: DEFAULT_TEAM_NAME, ladder: ladder || {} }], settings });
}

/** Forget one slot entirely. This is what "Start over" does. */
export function clearEntry(leagueName) {
  try {
    localStorage.removeItem(slotKey(leagueName));
  } catch {
    /* ignore */
  }
}

/**
 * The league name the last visit was working in, or null for the manual slot.
 *
 * Stored as the name rather than as an index, because the saved-league list can
 * be reordered or added to between visits and an index would then restore a
 * different league's roster under this league's settings.
 */
export function readActiveSlot() {
  try {
    const v = localStorage.getItem(RATE_ACTIVE_KEY);
    return v && v !== MANUAL_SLOT ? v : null;
  } catch {
    return null;
  }
}

export function writeActiveSlot(leagueName) {
  try {
    localStorage.setItem(RATE_ACTIVE_KEY, leagueName || MANUAL_SLOT);
  } catch {
    /* ignore */
  }
}

/**
 * The tab the last visit ended on, or null.
 *
 * Null rather than a default, so the CALLER owns what the default is. The
 * league tab is the default and must stay it (RatePage's header says why), and
 * a store that quietly returned "league" would hide a stored value that failed
 * to read.
 */
export function readTab() {
  try {
    const v = localStorage.getItem(RATE_TAB_KEY);
    return v === "league" || v === "team" ? v : null;
  } catch {
    return null;
  }
}

export function writeTab(tab) {
  try {
    if (tab === "league" || tab === "team") localStorage.setItem(RATE_TAB_KEY, tab);
  } catch {
    /* ignore */
  }
}

/**
 * Has this browser unlocked the full breakdown?
 *
 * A storage failure reads FALSE, which shows the gate to somebody who may have
 * unlocked it before. That is the right way round: the alternative is unlocking
 * for everybody whose browser blocks storage, and the gated content is the
 * thing the address is for. The cost of the wrong answer here is one form a
 * person can fill in again; the cost the other way is the gate not existing.
 */
export function isRateUnlocked() {
  if (isGateBypassed()) return true;
  try {
    return localStorage.getItem(RATE_UNLOCK_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Whether the gate is off for THIS device because it is one of ours.
 *
 * 🔴 IT REUSES THE INTERNAL FLAG RATHER THAN INVENTING A BYPASS. Ben, Sep 1
 * 2026: "the email gates stop me from testing... we need a way to skip it for
 * when I'm testing." The obvious move is a ?skipgate=1 URL param, and it is the
 * wrong one: a guessable parameter on a production site is not a testing aid,
 * it is the gate not existing for anybody who reads a support thread.
 *
 * analyticsGate.js already solved this exact problem. `?internal=1` writes a
 * persistent flag that keeps Ben's own production devices out of PostHog, and
 * "this device is ours" is precisely the question being asked here. So the gate
 * reads the same flag. No new parameter, no new surface, and a device that is
 * already marked internal needs nothing done to it at all.
 *
 * Localhost is included because a dev server has no business gating anything,
 * and because saved leagues are per-origin: the leagues Ben tests against live
 * on mock1.app, which is why he tests there in the first place.
 */
export function isGateBypassed() {
  // 🔴 THE HOST BYPASS IS OFF UNDER TEST, AND THIS IS NOT A TECHNICALITY.
  // jsdom serves every test from http://localhost/, so isLocalHost() is true in
  // the entire suite - which silently unlocked the gate for all fifteen tests
  // that exist to prove the gate works. A bypass that disables the tests
  // guarding the thing it bypasses is worse than no bypass: the gate could
  // break afterwards and nothing would say so.
  //
  // The FLAG still works under test, deliberately. A test that wants to check
  // the bypass sets the internal flag and gets it; every other test sees the
  // gate exactly as a visitor does.
  if (process.env.NODE_ENV === "test") return isInternalUser();
  return isLocalHost() || isInternalUser();
}

/** Record the unlock. Never called on anything but a successful signup. */
export function markRateUnlocked() {
  try {
    localStorage.setItem(RATE_UNLOCK_KEY, "1");
  } catch {
    /* the unlock still holds for this render; it simply does not persist */
  }
}

/**
 * Where a PASTED league lives between renders.
 *
 * 🔴 IT IS ITS OWN KEY BECAUSE IT ANSWERS A DIFFERENT QUESTION. The team slot
 * already remembers WHAT was entered; this remembers that the room arrived as
 * one paste and which screen the reader was on. Ben, Sep 1 2026: "If I click
 * away I lose the league." He did not lose the rosters - those were saved all
 * along - he lost the VIEW, because the step is derived from the ladder on
 * mount and a filled ladder derives to the entry form. A pasted room has to
 * come back as a pasted room.
 *
 * `step` is stored here and nowhere else, so ordinary hand entry keeps deriving
 * its step exactly as it did before: nothing about the typed path changes.
 */
export const RATE_PASTE_KEY = "auctionArchitect_ratePaste";

const PASTE_STEPS = new Set(["settings", "roster", "report", "room"]);

/** The live pasted league, or null. */
export function readPaste() {
  try {
    const raw = JSON.parse(localStorage.getItem(RATE_PASTE_KEY));
    if (!raw || typeof raw !== "object" || raw.live !== true) return null;
    return {
      live: true,
      step: PASTE_STEPS.has(raw.step) ? raw.step : "room",
      active: Number.isInteger(raw.active) && raw.active >= 0 ? raw.active : 0,
      proven: Array.isArray(raw.proven) ? raw.proven.filter((p) => typeof p === "string") : [],
    };
  } catch {
    return null;
  }
}

/** Record it. A failure leaves the page working, unremembered. */
export function writePaste({ step, active, proven }) {
  try {
    localStorage.setItem(
      RATE_PASTE_KEY,
      JSON.stringify({ live: true, step, active: active || 0, proven: proven || [] })
    );
  } catch {
    /* ignore */
  }
}

/** Forget it. Called when the room is cleared, never on a tab switch. */
export function clearPaste() {
  try {
    localStorage.removeItem(RATE_PASTE_KEY);
  } catch {
    /* ignore */
  }
}
